import type { ReactNode, Ref } from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";

import arrowDown from "@/assets/figma/top-navigation/arrow-down.svg";
import statusRing from "@/assets/figma/top-navigation/status-ring.svg";
import storeBackplate from "@/assets/figma/top-navigation/store-backplate.svg";
import { cn } from "@/shared/lib/cn";
import {
  getMerryFunctionButtonFigmaNodeId,
  type MerryFunctionButtonVariant,
} from "@/shared/ui/merryTopNavigationVariants";
import { useMerryButtonInteractionState } from "@/shared/ui/useMerryButtonInteractionState";

interface MerryFunctionButtonBaseProps
  extends Omit<AriaButtonProps, "aria-label" | "children" | "className"> {
  "aria-label": string;
  className?: string;
  label: string;
  triggerRef?: Ref<HTMLButtonElement>;
  value: string;
}

interface MerryAccountFunctionButtonProps
  extends MerryFunctionButtonBaseProps {
  avatarSource: string;
  statusRingSource?: string;
  variant: "account";
}

interface MerryStoreFunctionButtonProps extends MerryFunctionButtonBaseProps {
  avatarSources: readonly [string, string, string];
  countryCode?: string;
  platformBadge?: string;
  provider?: string;
  variant: "store";
}

export type MerryFunctionButtonProps =
  | MerryAccountFunctionButtonProps
  | MerryStoreFunctionButtonProps;

export function MerryFunctionButton(props: MerryFunctionButtonProps) {
  if (props.variant === "account") {
    const { avatarSource, statusRingSource, variant, ...buttonProps } = props;

    return (
      <MerryFunctionButtonBase
        {...buttonProps}
        variant={variant}
        visuals={
          <AccountStatus
            avatarSource={avatarSource}
            statusRingSource={statusRingSource}
          />
        }
      />
    );
  }

  const { avatarSources, countryCode, platformBadge, provider, variant, ...buttonProps } = props;

  return (
    <MerryFunctionButtonBase
      {...buttonProps}
      variant={variant}
      visuals={<StoreStatus avatarSources={avatarSources} countryCode={countryCode} platformBadge={platformBadge} provider={provider} />}
    />
  );
}

function MerryFunctionButtonBase({
  "aria-label": ariaLabel,
  className,
  isDisabled,
  label,
  onHoverChange,
  onPressChange,
  triggerRef,
  value,
  variant,
  visuals,
  ...props
}: MerryFunctionButtonBaseProps & {
  variant: Exclude<MerryFunctionButtonVariant, "logo">;
  visuals: ReactNode;
}) {
  const interaction = useMerryButtonInteractionState({
    isDisabled,
    onHoverChange,
    onPressChange,
  });

  return (
    <AriaButton
      {...props}
      aria-label={ariaLabel}
      className={({ isFocusVisible }) =>
        cn(
          "merry-function-button",
          isFocusVisible && "merry-function-button--focus-visible",
          isDisabled && "merry-function-button--disabled",
          className,
        )
      }
      data-function-button-state={interaction.interactionState}
      data-function-button-variant={variant}
      data-figma-component-id="300:31797"
      data-figma-node-id={getMerryFunctionButtonFigmaNodeId(
        variant,
        interaction.interactionState,
      )}
      isDisabled={isDisabled}
      onHoverChange={interaction.onHoverChange}
      onPressChange={interaction.onPressChange}
      ref={triggerRef}
      type="button"
    >
      <span className="merry-function-button__content">
        <span className="merry-function-button__copy">
          <span className="merry-function-button__label">{label}</span>
          <span className="merry-function-button__value">{value}</span>
        </span>

        <span aria-hidden="true" className="merry-function-button__visuals">
          {visuals}
          <span className="merry-function-button__arrow">
            <img alt="" src={arrowDown} />
          </span>
        </span>
      </span>
    </AriaButton>
  );
}

function AccountStatus({
  avatarSource,
  statusRingSource = statusRing,
}: {
  avatarSource: string;
  statusRingSource?: string;
}) {
  return (
    <span
      className="merry-function-button__account-status"
      data-function-status-ring="fixed"
    >
      <AvatarImage source={avatarSource} />
      <img
        alt=""
        className="merry-function-button__status-ring"
        src={statusRingSource}
      />
    </span>
  );
}

function StoreStatus({
  avatarSources,
  countryCode,
  platformBadge,
  provider,
}: {
  avatarSources: readonly [string, string, string];
  countryCode?: string;
  platformBadge?: string;
  provider?: string;
}) {
  return (
    <span className="merry-function-button__store-status">
      <span className="merry-function-button__store-pair">
        <StoreAvatar countryCode={countryCode} kind="country" source={avatarSources[0]} />
        <StoreAvatar kind="platform" platformBadge={platformBadge} provider={provider} source={avatarSources[1]} />
      </span>
      <StoreAvatar kind="store" source={avatarSources[2]} />
    </span>
  );
}

function StoreAvatar({
  countryCode,
  kind,
  platformBadge,
  provider,
  source,
}: {
  countryCode?: string;
  kind: "country" | "platform" | "store";
  platformBadge?: string;
  provider?: string;
  source: string;
}) {
  return (
    <span
      className="merry-function-button__store-avatar"
      data-store-avatar-kind={kind}
    >
      {kind === "store" ? (
        <img
          alt=""
          className="merry-function-button__store-backplate"
          src={storeBackplate}
        />
      ) : null}
      {countryCode && kind === "country" ? (
        <span className={`flag:${countryCode} merry-function-button__country-flag`} />
      ) : platformBadge && kind === "platform" ? (
        <span className="merry-function-button__platform-badge" data-provider={provider}>{platformBadge}</span>
      ) : source ? <AvatarImage source={source} /> : null}
    </span>
  );
}

function AvatarImage({ source }: { source: string }) {
  return (
    <span className="merry-function-button__avatar-image">
      <img alt="" src={source} />
    </span>
  );
}

export function MerryBrandLockup({ agencyName }: { agencyName?: string }): ReactNode {
  const visibleAgencyName = agencyName?.trim();
  return (
    <>
      <span className="merry-brand-lockup__merry">MERRY</span>
      {visibleAgencyName ? <>
        <span className="merry-brand-lockup__secondary merry-brand-lockup__x">X</span>
        <span className="merry-brand-lockup__secondary merry-brand-lockup__agency" title={visibleAgencyName}>{visibleAgencyName}</span>
      </> : null}
    </>
  );
}
